import { useContext, useEffect, useState } from 'react'
import styled from '@emotion/styled'
import { useMutation } from '@apollo/client'
import { Alert, Button, space } from '@ticketswap/solar'
import { Facebook } from '@ticketswap/solar/icons'
import { AUTHENTICATION_STARTED, AUTHENTICATION_COMPLETED } from '~/lib/tracking-events'
import { track, setTrackingIdentity } from '~/utils/analytics'
import { AUTHENTICATE_CLIENT_FOR_TOKEN } from '~/graphql/mutations/authenticateClientForToken'
import { FacebookContext } from '~/components/FacebookContext'
import { AUTHENTICATE_USER_WITH_FACEBOOK } from '~/graphql/mutations/authenticateUserWithFacebook'
import getConfig from 'next/config'
import { setCookie } from 'cookies-next'
import { MONTH_IN_SECONDS } from '~/lib/timeInSeconds'

const { publicRuntimeConfig } = getConfig()

const StyledAlert = styled(Alert)`
  margin-block-end: ${space[8]};
`

const FacebookAuthButton = ({ children, t, onSignup, onLogin }) => {
  const [errorMessage, setErrorMessage] = useState(null)
  const [loadingFacebook, setLoadingFacebook] = useState(false)
  const { initialize, isInitialized, login } = useContext(FacebookContext)
  const [generate, { client }] = useMutation(AUTHENTICATE_CLIENT_FOR_TOKEN)
  const [authenticate] = useMutation(AUTHENTICATE_USER_WITH_FACEBOOK)

  useEffect(() => {
    if (!isInitialized) {
      initialize()
    }
  }, [initialize, isInitialized])

  const handleCompleted = async facebookData => {
    const {
      data: { authenticateClientForToken },
    } = await generate({
      variables: {
        input: {
          clientId: publicRuntimeConfig.NEXT_PUBLIC_CLIENT_ID,
        },
      },
    })

    const {
      data: { facebookAuth },
    } = await authenticate({
      variables: {
        input: {
          token: authenticateClientForToken.token,
          facebookAccessToken: facebookData.accessToken,
        },
      },
    })
    const { tokenString } = facebookAuth.apiAccessToken

    setCookie(process.env.NEXT_PUBLIC_TOKEN_COOKIE_NAME, tokenString, {
      maxAge: MONTH_IN_SECONDS,
      secure: true,
      sameSite: 'lax',
    })

    await client.resetStore()

    await setTrackingIdentity()

    if (facebookAuth.isNew) {
      return onSignup({
        firstName: facebookData.profile.first_name,
        lastName: facebookData.profile.last_name,
      })
    }

    track(AUTHENTICATION_COMPLETED, {
      method: 'facebook',
      isNew: false,
    })

    onLogin()
  }

  const handleError = error => {
    setLoadingFacebook(false)
    let message = ''

    if (error.message === 'We were unable to authenticate you on Facebook') {
      message = t('common:auth.login.facebook.errors.not_authenticated')
    } else if (error.message === 'We were unable to retrieve your profile from Facebook') {
      message = t('common:auth.login.facebook.errors.not_authorized')
    } else {
      message = t('common:auth.login.facebook.errors.generic')
    }

    track(AUTHENTICATION_COMPLETED, {
      method: 'facebook',
      error: error.message,
    })

    setErrorMessage(message)
  }

  return (
    <>
      {errorMessage && <StyledAlert variant="error">{errorMessage}</StyledAlert>}
      <Button
        loading={loadingFacebook}
        disabled={!isInitialized}
        variant="facebook"
        fullWidth
        leftAdornment={<Facebook size={24} />}
        onClick={async () => {
          setLoadingFacebook(true)
          track(AUTHENTICATION_STARTED, {
            method: 'facebook',
          })

          try {
            const response = await login()
            await handleCompleted(response)
          } catch (error) {
            handleError(error)
          }
        }}
      >
        {children}
      </Button>
    </>
  )
}

export default FacebookAuthButton
